import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { AssetCameraKey, AssetId, CameraId } from "@visioni/a3s-schema";
import type {
  ListActivityResponse,
  ListActivityQuery,
  ActivityEdgeResponse,
  ActivityListItem,
} from "../../client-messages/list-activity";
import { listMyActivityEndpoint } from "../../client-messages/list-activity";
import { nextActivityOffset } from "../helpers/next-activity-offset";

const NOTIFICATIONS_PAGE_SIZE = 10;
const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000;

export interface UseActivityNotificationsOptions {
  /** Whitelist of activity kinds to include. If empty/undefined, no filter is applied. */
  kinds?: string[];
  enabled?: boolean;
}

export interface ActivityNotificationItem {
  id: string;
  kind: ActivityListItem["kind"];
  summary: string;
  actorEmail: string | null;
  siteId: string | null;
  cameraKey: string | null;
  edge: ActivityEdgeResponse | null;
  createdAt: string;
}

export interface ActivityNotificationsResult {
  readonly items: readonly ActivityNotificationItem[];
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
}

function toNotificationItem(item: ListActivityResponse["data"][number]): ActivityNotificationItem {
  const cameraKey = buildCameraKey(item.assetId, item.cameraId);
  return {
    id: item.id,
    kind: item.kind,
    summary: item.summary,
    actorEmail: item.actorEmail,
    siteId: item.siteId,
    cameraKey,
    edge: item.edge,
    createdAt: item.createdAt,
  };
}

function buildCameraKey(assetId: string | null, cameraId: string | null): string | null {
  if (!assetId || !cameraId) return null;
  try {
    const key = AssetCameraKey.fromParts(AssetId.fromString(assetId), CameraId.fromString(cameraId));
    return AssetCameraKey.toString(key);
  } catch {
    return null;
  }
}

export function useActivityNotifications(options: UseActivityNotificationsOptions = {}): ActivityNotificationsResult {
  const { kinds, enabled = true } = options;
  const apiConfig = useApiConfig();
  const { tenant, authenticatedFetch } = apiConfig;

  const query = useInfiniteQuery<
    ListActivityResponse,
    Error,
    InfiniteData<ListActivityResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: ["activity", "notifications", tenant?.tenantId, kinds] as const,
    enabled: enabled && !!tenant,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => nextActivityOffset(lastPage, lastPageParam),
    queryFn: async ({ pageParam, signal }) => {
      if (!tenant) throw new Error("Tenant not ready");

      const queryParams: ListActivityQuery = {
        kinds: kinds && kinds.length > 0 ? kinds.join(",") : undefined,
        limit: String(NOTIFICATIONS_PAGE_SIZE),
        offset: String(pageParam),
      };

      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listMyActivityEndpoint, { path: { tenantId: tenant.tenantId }, query: queryParams });

      const response = await authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              if (json.message) message = json.message;
              else message = text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with default message
        }
        throw new Error(message);
      }

      const data: unknown = await response.json();
      return listMyActivityEndpoint.responseSchema.parse(data);
    },
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const items = useMemo<readonly ActivityNotificationItem[]>(() => {
    const all = query.data?.pages.flatMap((page) => page.data.map(toNotificationItem)) ?? [];
    const seen = new Set<string>();
    return all.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [query.data]);

  return {
    items,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
